<template>
    <vx-popover :hover="props.hover" :placement="props.placement" arrow @open="fetchData" :teleport="props.teleport" :zIndex="testing ? 10 : undefined">
        <slot>
            <button
                type="button"
                class="cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                :aria-label="isCampaign ? 'Campaign details' : 'Automation details'"
                data-test="resource-details-trigger"
            >
                <font-awesome-icon widthAuto class="text-slate-500" :icon="faCircleInfo" />
            </button>
        </slot>

        <template v-slot:content>
            <div v-if="error && name" class="flex flex-col max-w-lg gap-2">
                <resource-name
                    :icon="props.nameIcon"
                    class="flex-row-reverse"
                    :modelValue="{ ...props.modelValue, name }"
                ></resource-name>
                <div class="text-slate-500">
                    Failed to load additional information.
                </div>
            </div>
            <div v-else-if="error && !name" class="max-w-lg text-slate-500">
                Failed to load additional information.
            </div>
            <div v-else-if="loading" class="px-40 py-14">
                <vx-icon-loading spin></vx-icon-loading>
            </div>
            <div v-else-if="loaded" class="flex flex-col max-w-lg gap-2">
                <resource-name
                    :icon="props.nameIcon"
                    class="flex-row-reverse"
                    :modelValue="{ ...props.modelValue, name}"
                ></resource-name>

                <div class="grid grid-cols-5 gap-2">
                    <template v-if="isCampaign">
                        <div class="col-span-1">Who:</div>
                        <div class="col-span-4 line-clamp-1" data-test="to">
                            <span v-if="resourceDetail?.audience_description">{{ resourceDetail.audience_description }}</span>
                            <span v-else>-</span>
                        </div>
                    </template>
                    <template v-if="mainMessage">
                        <div class="col-span-1">What:</div>
                        <div class="col-span-4" data-test="what">
                            <div class="flex flex-col gap-2">
                                <p class="break-words line-clamp-9">
                                    {{ mainMessage.body }}
                                </p>
                                <div class="flex items-center gap-2">
                                    <vx-button
                                        size="sm"
                                        color="muted-light"
                                        outline
                                        @click="copyText(mainMessage.body)"
                                    >
                                        Copy Full Text
                                        <font-awesome-icon widthAuto :icon="faCopy" />
                                    </vx-button>
                                    <vx-button
                                        v-if="!hideSendButton"
                                        size="sm"
                                        color="muted"
                                        @click="testing = true"
                                        data-test="send-test"
                                    >
                                        Send Test
                                        <font-awesome-icon widthAuto
                                            :icon="faFlaskVial"
                                        ></font-awesome-icon>
                                    </vx-button>

                                    <vx-button
                                        v-if="mainMessage.media_url"
                                        :href="mainMessage.media_url"
                                        target="_blank"
                                        size="sm"
                                        color="muted"
                                        data-test="view-attachment"
                                    >
                                        View Attachment
                                        <font-awesome-icon widthAuto
                                            :icon="faDownload"
                                        ></font-awesome-icon>
                                    </vx-button>

                                    <message-builder-test
                                        v-model:visible="testing"
                                        :groupId="props.groupId"
                                        :orgUnitId="props.orgUnitId"
                                        :bodyMessage="mainMessage.body"
                                        :mediaUrl="mainMessage.media_url"
                                        :mediaContentType="
                                        mainMessage.media_content_type
                                    "
                                    ></message-builder-test>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-if="isCampaign">
                        <div class="col-span-1">When:</div>
                        <span class="col-span-4 flex items-center gap-2">
                            <template v-if="resourceDetail?.launched_at">
                                <span>{{ fromNow(resourceDetail.launched_at)}}</span>
                                <span>-</span>
                                <span>{{ shortDateTime(resourceDetail.launched_at)}}</span>
                            </template>
                            <span v-else> - </span>
                        </span>
                    </template>
                </div>
            </div>
        </template>
    </vx-popover>
</template>
<script setup>
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import {
    faCopy,
    faDownload,
    faFlaskVial,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import MessageBuilderTest from '~/components/general/message-builder/modals/MessageBuilderTest.vue';
import {
    VxButton,
    VxPopover,
    VxIconLoading,
} from '@voxie/frontend-components';
import { fromNow, shortDateTime } from '~/utils/date';
import ResourceName from '~/components/general/resources/ResourceName.vue';
import { computed, ref, watch } from 'vue';
import { useCopyText } from '~/composables/useCopyText';
import { useResourcesDetails } from '~/components/analytics/useResourcesDetails';
import { isCampaign as checkIfCampaign } from '~/utils/analytics';
import axios from 'axios';


const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: false,
    },
    groupId: {
        type: String,
        required: false,
    },
    modelValue: {
        type: Object,
        required: true,
    },
    nameIcon: {
        type: Boolean,
        default: false,
    },
    hover: {
        type: Boolean,
        default: true,
    },
    placement: {
        type: String,
        default: 'top',
    },
    teleport: {
        type: Boolean,
        default: true,
    },
    hideSendButton: {
        type: Boolean,
        default: false,
    }
});

const { copyText } = useCopyText();

const testing = ref(false);
const loading = ref(false);
const loadingId = ref();
const loaded = ref(false);
const error = ref(false);

const { getResourceDetail, fetchResourceDetails } = useResourcesDetails();

watch(() => props.modelValue, () => {
    loaded.value = false;
    error.value = false;
    loading.value = true;
}, {
    deep: true,
});


const mainMessage = computed(() => resourceDetail.value?.main_message);
const isCampaign = computed(() => checkIfCampaign(props.modelValue.resource_type));
const name = computed(() => props.modelValue.name || resourceDetail.value?.name);
const resourceDetail = computed(() => getResourceDetail(props.modelValue.resource_type, props.modelValue.id));

let detailsAbort;

const fetchData = () => {
    if (loadingId.value === props.modelValue.id && (loading.value || loaded.value)) {
        return;
    }

    detailsAbort?.abort()
    detailsAbort = new AbortController();

    loading.value = true;
    loadingId.value = props.modelValue.id;

    fetchResourceDetails(
        props.teamId,
        [{ resource_type: props.modelValue.resource_type, resource_id: props.modelValue.id }],
        props.groupId,
        props.orgUnitId,
        { signal: detailsAbort.signal }
    ).then(
        () => {
            loaded.value = true;
            loading.value = false;
        },
    ).catch((e) => {
        if (axios.isCancel(e)) {
            return;
        }

        error.value = true;
        loading.value = false;
    })
};
</script>
